import React from 'react'
import Select, {
  components,
  GroupBase,
  MenuListProps,
  MenuProps,
  Props as SelectProps,
  StylesConfig,
} from 'react-select'

export const INVENTORY_ITEM_MENU_BOTTOM_GAP = 24
export const INVENTORY_ITEM_MENU_MAX_HEIGHT = 260
export const INVENTORY_ITEM_MENU_MIN_HEIGHT = 120
const INVENTORY_ITEM_MENU_GUTTER = 8

export type InventoryItemMenuLayout = {
  maxMenuHeight: number
  menuPlacement: 'top' | 'bottom'
}

export type InventoryItemSelectExtraProps = {
  inventoryItemAnchorGetter?: () => HTMLElement | null
}

export type InventoryItemSelectProps<Option> = SelectProps<Option, false> &
  InventoryItemSelectExtraProps

const getViewportHeight = () =>
  window.visualViewport?.height ?? document.documentElement.clientHeight ?? window.innerHeight

export const getInventoryItemMenuLayout = (
  element: HTMLElement | null
): InventoryItemMenuLayout => {
  if (!element || typeof window === 'undefined') {
    return { maxMenuHeight: INVENTORY_ITEM_MENU_MAX_HEIGHT, menuPlacement: 'bottom' }
  }

  const rect = element.getBoundingClientRect()
  const viewportHeight = getViewportHeight()
  const spaceBelow =
    viewportHeight - rect.bottom - INVENTORY_ITEM_MENU_BOTTOM_GAP - INVENTORY_ITEM_MENU_GUTTER
  const spaceAbove = rect.top - INVENTORY_ITEM_MENU_BOTTOM_GAP - INVENTORY_ITEM_MENU_GUTTER
  const openUp =
    spaceBelow < INVENTORY_ITEM_MENU_MIN_HEIGHT ||
    (spaceBelow < spaceAbove && spaceBelow < INVENTORY_ITEM_MENU_MAX_HEIGHT)
  const available = Math.floor(openUp ? spaceAbove : spaceBelow)

  return {
    maxMenuHeight: Math.max(
      INVENTORY_ITEM_MENU_MIN_HEIGHT,
      Math.min(INVENTORY_ITEM_MENU_MAX_HEIGHT, available)
    ),
    menuPlacement: openUp ? 'top' : 'bottom',
  }
}

export const InventoryItemSelectMenuList = <
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  props: MenuListProps<Option, IsMulti, Group>
) => {
  const getAnchor = (props.selectProps as InventoryItemSelectExtraProps).inventoryItemAnchorGetter
  const { maxMenuHeight } = getInventoryItemMenuLayout(getAnchor?.() ?? null)

  return (
    <components.MenuList
      {...props}
      innerProps={{
        ...props.innerProps,
        style: {
          ...(props.innerProps?.style ?? {}),
          maxHeight: maxMenuHeight,
        },
      }}
    />
  )
}

export const InventoryItemSelectMenu = <
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  props: MenuProps<Option, IsMulti, Group>
) => {
  const getAnchor = (props.selectProps as InventoryItemSelectExtraProps).inventoryItemAnchorGetter
  const { maxMenuHeight, menuPlacement } = getInventoryItemMenuLayout(getAnchor?.() ?? null)

  return (
    <components.Menu
      {...props}
      placement={menuPlacement}
      innerProps={{
        ...props.innerProps,
        style: {
          ...(props.innerProps?.style ?? {}),
          maxHeight: maxMenuHeight,
          overflow: 'hidden',
        },
      }}
    />
  )
}

export const inventoryItemSelectMenuProps = {
  menuPortalTarget: typeof document !== 'undefined' ? document.body : null,
  menuPosition: 'fixed' as const,
  menuShouldScrollIntoView: false,
  captureMenuScroll: false,
  components: {
    Menu: InventoryItemSelectMenu,
    MenuList: InventoryItemSelectMenuList,
  },
}

export const createInventoryItemSelectStyles = <
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  getAnchor: () => HTMLElement | null,
  extraStyles: StylesConfig<Option, IsMulti, Group> = {}
): StylesConfig<Option, IsMulti, Group> => {
  const { maxMenuHeight } = getInventoryItemMenuLayout(getAnchor())

  return {
    ...extraStyles,
    menuPortal: (base, state) => ({
      ...(typeof extraStyles.menuPortal === 'function'
        ? extraStyles.menuPortal(base, state)
        : base),
      zIndex: 9999,
    }),
    menu: (base, state) => ({
      ...(typeof extraStyles.menu === 'function' ? extraStyles.menu(base, state) : base),
      maxHeight: maxMenuHeight,
      overflow: 'hidden',
    }),
    menuList: (base, state) => ({
      ...(typeof extraStyles.menuList === 'function'
        ? extraStyles.menuList(base, state)
        : base),
      maxHeight: maxMenuHeight,
    }),
  }
}

export function InventoryItemSelect<Option>(props: InventoryItemSelectProps<Option>) {
  return <Select<Option, false> {...inventoryItemSelectMenuProps} {...props} />
}
